import { DOWNLOADS_PAGE_URL, checkMessageScam, completeUnsubscribeMailto, createUnsubscribeJobs, findNewerReleaseVersion, getAiBetaAccess, getMailServerType, getKerioSpamSettings, getPairedAccount, getSettings, getUnsubscribeJob, listPairedAccounts, listUnsubscribeJobs, LOGIN_URL, accountsMatch, matchPairedAccount, normalizeIgnoredMailboxIdentity, pairDevice, scamResultFromJob, setKerioSpamSettings, submitScamCheckFeedback, isScamCheckAllowed, SCAM_HEURISTIC_FIX_PROMPT, unpair, updateCustomScore, updateList, updateSettings, warmSettingsCache, } from "@apps/client-sdk";
import { APP_SECTIONS, renderArchivingSection, renderSieveSection } from "./sections/index.js";
import { renderKerioSpamSection } from "./sections/sieve/kerio/kerioSpamPanel.js";
import { renderKerioDavHelperSection } from "./sections/sieve/kerio/kerioDavHelper.js";
import { showConfirmDialog } from "./confirmDialog.js";
import { showOkLocalesPicker } from "./localePicker.js";
const DENY_LIST_LIMIT = 25;
const DENY_LIST_HINTS = [
    "Using a deny list is great for blocking a specific, known person, but it won't stop real spammers who constantly rotate through new, fake email addresses. Trying to block every spammer manually just creates a massive list that bogs down your server's performance. To keep things fast and efficient, we limit the deny list to 25 addresses, ensuring it is used strictly for persistent individual senders.",
    "For spam, the best approach is to drag the email into your Junk folder; this automatically trains the server's filters to recognize and reports the message to several spam organizations.",
];
function scoreOptions(current) {
    const options = [];
    for (let score = 3.0; score <= 10.0; score += 0.5) {
        options.push(score.toFixed(1));
    }
    if (current && current !== "" && !options.includes(current)) {
        options.push(current);
        options.sort((a, b) => parseFloat(a) - parseFloat(b));
    }
    return options;
}
function parseListValues(value) {
    return value
        .split(/\s+/)
        .map((v) => v.trim())
        .filter((v) => v !== "");
}
function sortedListValues(value) {
    return parseListValues(value).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}
function senderListIncludes(items, email) {
    const lower = email.toLowerCase();
    return items.some((item) => item.toLowerCase() === lower);
}
function oppositeSenderListKey(prefKey) {
    if (prefKey === "whitelist_from")
        return "blacklist_from";
    if (prefKey === "blacklist_from")
        return "whitelist_from";
    return null;
}
function customScores(settings) {
    const scores = [];
    for (const [key, value] of Object.entries(settings)) {
        if (key.startsWith("score ")) {
            scores.push([key.slice(6), String(value)]);
        }
    }
    scores.sort(([a], [b]) => a.localeCompare(b));
    return scores;
}
function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className)
        node.className = className;
    if (text !== undefined)
        node.textContent = text;
    return node;
}
let flashClearTimer = null;
function flash(root, message, type, autoHideMs = type === "error" ? 8000 : 5000) {
    if (flashClearTimer !== null) {
        clearTimeout(flashClearTimer);
        flashClearTimer = null;
    }
    const existing = root.querySelector(".flash");
    existing?.remove();
    const node = el("div", `flash flash-${type}`, message);
    root.prepend(node);
    if (autoHideMs > 0) {
        flashClearTimer = setTimeout(() => {
            flashClearTimer = null;
            if (node.isConnected) {
                node.remove();
            }
        }, autoHideMs);
    }
}
function selectedCheckboxValues(container, selector) {
    return [...container.querySelectorAll(selector)]
        .filter((input) => input.checked)
        .map((input) => input.value);
}
function wireRemoveSelectedButton(container, checkboxSelector, removeBtn) {
    const sync = () => {
        const count = container.querySelectorAll(`${checkboxSelector}:checked`).length;
        removeBtn.disabled = count === 0;
    };
    container.addEventListener("change", (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches(checkboxSelector)) {
            sync();
        }
    });
    sync();
}
/** Invert every matching checkbox (checked ↔ unchecked), then notify change listeners. */
function createToggleAllButton(container, checkboxSelector) {
    const toggleBtn = el("button", "btn btn-secondary btn-sm", "Toggle all");
    toggleBtn.type = "button";
    toggleBtn.addEventListener("click", () => {
        const boxes = [
            ...container.querySelectorAll(checkboxSelector),
        ];
        for (const input of boxes) {
            input.checked = !input.checked;
        }
        boxes[0]?.dispatchEvent(new Event("change", { bubbles: true }));
    });
    return toggleBtn;
}
function createSelectableListItem(value, label) {
    const li = el("li");
    const itemLabel = el("label", "list-item-check");
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "list-select-checkbox";
    checkbox.value = value;
    itemLabel.append(checkbox, el("span", undefined, label ?? value));
    li.append(itemLabel);
    return li;
}
export class EmailAppsApp {
    options;
    activeSection = "spamassassin";
    settings = {};
    mailServerType = "dovecot";
    messageContext = null;
    accountName = null;
    pairedAccounts = [];
    accountAliases = {};
    ignoredMailboxIdentities = [];
    manualAccountOverride = false;
    lastMailboxAccount = null;
    /** When true, alias add/remove re-renders the addresses page instead of the dashboard. */
    showingMailboxAddresses = false;
    /** Keeps Thunderbird/popup height from shrinking when switching to a shorter section. */
    lockedDashboardMinHeight = 0;
    unsubscribeJobs = [];
    /** Collapsed by default; remembered across dashboard re-renders. */
    unsubscribeStatusExpanded = false;
    /** Last on-demand scam check result for the Current Message card. */
    scamCheckResult = null;
    scamCheckError = null;
    scamFeedbackSubmittedLabel = null;
    openDisagreementCount = 0;
    /** When set, the Current Message card is highlighting this activity job. */
    focusedActivityJobId = null;
    /** True when the top card is showing a job opened from Message activity. */
    viewingSelectedActivity = false;
    constructor(options) {
        this.options = options;
    }
    lockDashboardHeight() {
        const { root } = this.options;
        const measured = Math.ceil(root.getBoundingClientRect().height);
        if (measured > this.lockedDashboardMinHeight) {
            this.lockedDashboardMinHeight = measured;
        }
        if (this.lockedDashboardMinHeight > 0) {
            root.style.minHeight = `${this.lockedDashboardMinHeight}px`;
        }
    }
    async syncPairedAccounts() {
        this.pairedAccounts = await listPairedAccounts(this.options.storage);
        await this.loadAccountAliases();
        await this.loadIgnoredMailboxIdentities();
    }
    async loadAccountAliases() {
        const aliases = {};
        for (const account of this.pairedAccounts) {
            aliases[account] = await this.options.storage.listMailboxAliases(account);
        }
        this.accountAliases = aliases;
    }
    async loadIgnoredMailboxIdentities() {
        this.ignoredMailboxIdentities =
            await this.options.storage.listIgnoredMailboxIdentities();
    }
    isMailboxIdentityIgnored() {
        const identities = [
            ...this.mailboxCandidates(),
            this.messageContext?.mailboxLabel,
            this.messageContext?.mailboxAccount,
        ].filter((value) => Boolean(value?.trim()));
        return identities.some((identity) => {
            try {
                return this.ignoredMailboxIdentities.includes(normalizeIgnoredMailboxIdentity(identity));
            }
            catch {
                return false;
            }
        });
    }
    mailboxCandidates() {
        const context = this.messageContext;
        if (!context) {
            return [];
        }
        if (context.mailboxCandidates && context.mailboxCandidates.length > 0) {
            return context.mailboxCandidates;
        }
        if (context.mailboxAccount) {
            return [context.mailboxAccount];
        }
        return [];
    }
    matchingMailboxAccount() {
        return matchPairedAccount(this.mailboxCandidates(), this.pairedAccounts, this.accountAliases);
    }
    primaryMailboxEmail() {
        return this.mailboxCandidates().find((candidate) => candidate.includes("@")) ?? null;
    }
    async autoSelectAccount() {
        const candidates = this.mailboxCandidates();
        const mailboxKey = candidates.join("\0");
        if (mailboxKey && mailboxKey !== this.lastMailboxAccount) {
            this.manualAccountOverride = false;
            this.lastMailboxAccount = mailboxKey;
        }
        const matched = this.matchingMailboxAccount();
        if (!this.manualAccountOverride && matched) {
            await this.options.storage.setActiveAccount(matched);
        }
        this.accountName = await getPairedAccount(this.options.storage);
    }
    async switchAccount(account) {
        this.manualAccountOverride = true;
        await this.options.storage.setActiveAccount(account);
        await this.loadDashboard();
    }
    async confirmRemoveActiveAccount() {
        const account = this.accountName;
        if (!account) {
            return;
        }
        const confirmed = await showConfirmDialog(this.options.root, {
            title: "Remove account?",
            message: `Remove ${account} from this device? You will need to pair again to manage its email apps settings.`,
            confirmLabel: "Remove account",
            cancelLabel: "Cancel",
            danger: true,
        });
        if (!confirmed) {
            return;
        }
        await this.removeActiveAccount();
    }
    async removeActiveAccount() {
        const account = this.accountName;
        if (!account) {
            return;
        }
        await this.options.settingsCache?.clear(account);
        await unpair(this.options.storage, account);
        await this.syncPairedAccounts();
        this.manualAccountOverride = false;
        if (this.pairedAccounts.length === 0) {
            this.renderPairing();
            return;
        }
        await this.loadDashboard();
    }
    async init() {
        await this.syncPairedAccounts();
        const code = this.normalizePairingCode(this.options.initialPairingCode);
        if (code) {
            const result = await this.tryPairWithCode(code);
            if (result.ok) {
                const loaded = await this.loadDashboard();
                if (loaded) {
                    flash(this.options.root, "Device paired successfully.", "success");
                }
                this.scheduleSettingsWarmUp();
                void this.showUpdateBannerIfNeeded();
                return;
            }
            if (this.pairedAccounts.length === 0) {
                this.renderPairing(code, result.error);
                return;
            }
            await this.loadDashboard();
            this.scheduleSettingsWarmUp();
            flash(this.options.root, result.error, "error");
            void this.showUpdateBannerIfNeeded();
            return;
        }
        if (this.options.initialView === "pairing") {
            if (this.pairedAccounts.length === 0) {
                this.renderPairing();
            }
            else {
                this.renderAddAccount();
            }
            this.scheduleSettingsWarmUp();
            void this.showUpdateBannerIfNeeded();
            return;
        }
        if (this.pairedAccounts.length === 0) {
            this.renderPairing();
            return;
        }
        await this.loadDashboard();
        this.scheduleSettingsWarmUp();
        void this.showUpdateBannerIfNeeded();
    }
    scheduleSettingsWarmUp() {
        if (!this.options.settingsCache || this.pairedAccounts.length === 0) {
            return;
        }
        void warmSettingsCache({
            storage: this.options.storage,
            settingsCache: this.options.settingsCache,
        });
    }
    normalizePairingCode(code) {
        const normalized = code?.trim().toUpperCase();
        if (!normalized || normalized.length !== 6) {
            return null;
        }
        return normalized;
    }
    async tryPairWithCode(code) {
        try {
            await pairDevice(code, this.options.storage);
            await this.syncPairedAccounts();
            return { ok: true };
        }
        catch (error) {
            return {
                ok: false,
                error: error instanceof Error ? error.message : "Pairing failed",
            };
        }
    }
    async showUpdateBannerIfNeeded() {
        if (this.options.autoUpdate) {
            return;
        }
        const installedVersion = this.options.installedVersion;
        if (!installedVersion) {
            return;
        }
        const newerVersion = await findNewerReleaseVersion(installedVersion);
        if (!newerVersion) {
            return;
        }
        const banner = el("div", "flash flash-success", `Version ${newerVersion} is available.`);
        if (this.options.reloadOnUpdate) {
            const reloadBtn = el("button", "btn btn-link", "Reload to update");
            reloadBtn.type = "button";
            reloadBtn.addEventListener("click", () => {
                void this.reloadForUpdate();
            });
            banner.append(document.createTextNode(" "), reloadBtn);
        }
        else {
            const link = el("a", "btn btn-link", "View downloads");
            link.href = DOWNLOADS_PAGE_URL;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            banner.append(document.createTextNode(" "), link);
        }
        this.options.root.prepend(banner);
    }
    async reloadForUpdate() {
        try {
            if ("serviceWorker" in navigator) {
                const registration = await navigator.serviceWorker.getRegistration();
                if (registration) {
                    await registration.unregister();
                }
            }
            if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((key) => caches.delete(key)));
            }
        }
        catch {
            // Reload anyway so the user is not stuck on a stale bundle.
        }
        const url = new URL(window.location.href);
        url.searchParams.set("updated", String(Date.now()));
        window.location.replace(url.toString());
    }
    openUrl(url) {
        if (this.options.openUrl) {
            this.options.openUrl(url);
            return;
        }
        window.open(url, "_blank");
    }
    installedVersionLabel() {
        const version = this.options.installedVersion?.trim();
        if (!version) {
            return null;
        }
        return version.startsWith("v") ? version : `v${version}`;
    }
    renderOpenFullPageButton() {
        if (!this.options.openFullPage) {
            return null;
        }
        const openFull = el("button", "btn btn-link", "Open pop-out window");
        openFull.type = "button";
        openFull.title = "Open Email Apps in a larger window";
        openFull.addEventListener("click", () => {
            this.options.openFullPage?.();
        });
        return openFull;
    }
    renderHeader(userBar) {
        const header = el("header", "topbar");
        const headerInner = el("div", "container topbar-inner");
        const titleWrap = el("div", "topbar-title");
        titleWrap.append(el("strong", undefined, "Email Apps"));
        const versionLabel = this.installedVersionLabel();
        if (versionLabel) {
            titleWrap.append(el("span", "topbar-version", versionLabel));
        }
        headerInner.append(titleWrap);
        if (userBar) {
            headerInner.append(userBar);
        }
        else {
            const openFull = this.renderOpenFullPageButton();
            if (openFull) {
                headerInner.append(openFull);
            }
        }
        header.append(headerInner);
        return header;
    }
    renderPairingForm(title, hint, onSuccess, onCancel, prefillCode, initialError) {
        const { root } = this.options;
        root.replaceChildren();
        const header = this.renderHeader();
        const main = el("main", "container");
        main.append(el("h2", undefined, title));
        main.append(el("p", "hint", hint));
        if (initialError) {
            main.append(el("div", "flash flash-error", initialError));
        }
        const card = el("section", "card");
        const steps = el("ol");
        steps.append(el("li", undefined, "Sign in at the portal and click Generate Pairing Code"));
        steps.append(el("li", undefined, "To pair a second account, sign out of the portal and sign in as that user first"));
        steps.append(el("li", undefined, "Enter the 6-character code below (expires in 15 minutes)"));
        card.append(steps);
        const openLink = el("button", "btn btn-secondary mt", "Open pairing site");
        openLink.type = "button";
        openLink.addEventListener("click", () => {
            // Keep a durable pairing window open on the pair/add-account form.
            this.options.openFullPage?.({ view: "pairing" });
            this.openUrl(LOGIN_URL);
        });
        card.append(openLink);
        const form = el("form");
        const label = el("label", undefined, "Pairing code");
        label.htmlFor = "pair-code";
        const input = document.createElement("input");
        input.type = "text";
        input.id = "pair-code";
        input.maxLength = 6;
        input.required = true;
        input.autocomplete = "off";
        input.style.textTransform = "uppercase";
        if (prefillCode) {
            input.value = prefillCode;
        }
        const actions = el("div", "inline-add mt");
        const submit = el("button", "btn btn-primary", "Pair device");
        submit.type = "submit";
        actions.append(submit);
        if (onCancel) {
            const cancel = el("button", "btn btn-secondary", "Back");
            cancel.type = "button";
            cancel.addEventListener("click", () => onCancel());
            actions.append(cancel);
        }
        form.append(label, input, actions);
        form.addEventListener("submit", async (event) => {
            event.preventDefault();
            submit.disabled = true;
            try {
                await pairDevice(input.value, this.options.storage);
                flash(root, "Device paired successfully.", "success");
                await onSuccess();
            }
            catch (error) {
                flash(root, error instanceof Error ? error.message : "Pairing failed", "error");
                submit.disabled = false;
            }
        });
        card.append(form);
        main.append(card);
        root.append(header, main);
    }
    renderPairing(prefillCode, initialError) {
        this.renderPairingForm("Pair this device", "Pair Thunderbird or the mobile app using a one-time code from the portal.", async () => {
            await this.syncPairedAccounts();
            await this.loadDashboard();
        }, undefined, prefillCode, initialError);
    }
    renderAddAccount() {
        this.renderPairingForm("Add another account", "Pair an additional mail account without removing your existing paired accounts.", async () => {
            await this.syncPairedAccounts();
            this.manualAccountOverride = false;
            await this.loadDashboard();
        }, () => {
            void this.loadDashboard();
        });
    }
    async loadDashboard() {
        await this.syncPairedAccounts();
        if (this.pairedAccounts.length === 0) {
            this.renderPairing();
            return false;
        }
        // Resolve mailbox identity from the client first so account auto-switch
        // works when opening the popup after changing Thunderbird account boxes.
        this.renderLoading();
        await this.refreshMessageContext();
        await this.autoSelectAccount();
        let account = this.accountName;
        if (!account) {
            this.renderPairing();
            return false;
        }
        try {
            const access = await getAiBetaAccess(this.options.storage);
            this.openDisagreementCount = access.openDisagreementCount;
        }
        catch {
            // Beta UI stays hidden until access can be confirmed.
        }
        try {
            this.mailServerType = await getMailServerType(this.options.storage);
        }
        catch {
            this.mailServerType = "dovecot";
        }
        if (this.mailServerType === "kerio") {
            try {
                await this.refreshUnsubscribeJobs();
                this.renderDashboard();
                return true;
            }
            catch (error) {
                const { root } = this.options;
                root.replaceChildren();
                const main = el("main", "container");
                main.append(el("h2", undefined, "Connection error"));
                main.append(el("p", "flash flash-error", error instanceof Error ? error.message : "Failed to load dashboard"));
                root.append(main);
                return false;
            }
        }
        const cached = await this.options.settingsCache?.load(account);
        if (cached) {
            this.settings = cached;
            this.renderDashboard();
        }
        else {
            this.renderLoading();
        }
        try {
            const settingsPromise = getSettings(this.options.storage);
            const jobsPromise = this.refreshUnsubscribeJobs();
            const next = await settingsPromise;
            this.settings = next;
            await this.options.settingsCache?.save(account, this.settings);
            await jobsPromise;
            const previousAccount = account;
            // Context may have changed while settings loaded; re-match once more.
            await this.refreshMessageContext();
            await this.autoSelectAccount();
            account = this.accountName;
            if (!account) {
                this.renderPairing();
                return false;
            }
            try {
                this.mailServerType = await getMailServerType(this.options.storage);
            }
            catch {
                this.mailServerType = "dovecot";
            }
            if (this.mailServerType === "kerio") {
                await this.refreshUnsubscribeJobs();
                this.renderDashboard();
                return true;
            }
            if (account !== previousAccount) {
                const switchedCached = await this.options.settingsCache?.load(account);
                if (switchedCached) {
                    this.settings = switchedCached;
                    this.renderDashboard();
                }
                const switched = await getSettings(this.options.storage);
                this.settings = switched;
                await this.options.settingsCache?.save(account, switched);
                await this.refreshUnsubscribeJobs();
                this.renderDashboard();
                return true;
            }
            // Always re-paint after message context loads so Current Message actions
            // (allow/deny/unsubscribe) appear without requiring a section switch.
            this.renderDashboard();
            return true;
        }
        catch (error) {
            const { root } = this.options;
            root.replaceChildren();
            const main = el("main", "container");
            main.append(el("h2", undefined, "Connection error"));
            main.append(el("p", "flash flash-error", error instanceof Error ? error.message : "Failed to load settings"));
            const retry = el("button", "btn btn-secondary", "Try again");
            retry.type = "button";
            retry.addEventListener("click", () => {
                void this.loadDashboard();
            });
            const remove = el("button", "btn btn-danger", "Remove account");
            remove.type = "button";
            remove.addEventListener("click", () => {
                void this.confirmRemoveActiveAccount();
            });
            const actions = el("div", "inline-add mt");
            actions.append(retry, remove);
            main.append(actions);
            root.append(main);
            return false;
        }
    }
    renderLoading() {
        const { root } = this.options;
        root.replaceChildren();
        const header = this.renderHeader();
        const main = el("main", "container");
        main.append(el("p", "muted", "Loading settings…"));
        root.append(header, main);
    }
    async refreshMessageContext() {
        this.messageContext = null;
        this.scamCheckResult = null;
        this.scamCheckError = null;
        this.scamFeedbackSubmittedLabel = null;
        this.focusedActivityJobId = null;
        this.viewingSelectedActivity = false;
        if (!this.options.getMessageContext) {
            return;
        }
        try {
            this.messageContext = await this.options.getMessageContext();
            this.hydrateScamResultFromJobs();
        }
        catch {
            this.messageContext = null;
        }
    }
    async refreshOpenDisagreementCount() {
        try {
            const access = await getAiBetaAccess(this.options.storage);
            this.openDisagreementCount = access.openDisagreementCount;
        }
        catch {
            // Keep last known count; button stays hidden if we never got one.
        }
    }
    normalizeMessageId(value) {
        if (!value)
            return null;
        const cleaned = value.trim().replace(/^<|>$/g, "").trim().toLowerCase();
        return cleaned || null;
    }
    jobMatchesMessage(job, ids) {
        const payload = job.payload;
        if (!payload)
            return false;
        const mid = this.normalizeMessageId(ids.messageId);
        const jobMid = this.normalizeMessageId(payload.message_id);
        // Prefer RFC Message-ID — Thunderbird client ids can be recycled.
        if (mid) {
            return Boolean(jobMid && mid === jobMid);
        }
        const clientId = ids.clientMessageId?.trim();
        if (!clientId || payload.client_message_id !== clientId) {
            return false;
        }
        const sender = ids.senderEmail?.trim().toLowerCase();
        const jobSender = payload.sender_email?.trim().toLowerCase();
        if (sender && jobSender) {
            return sender === jobSender;
        }
        // No sender on one side — allow client_message_id-only match.
        return true;
    }
    findActivityJob(jobType, ids) {
        return (this.unsubscribeJobs.find((job) => job.job_type === jobType && this.jobMatchesMessage(job, ids)) ?? null);
    }
    hydrateScamResultFromJobs() {
        // Don't override an intentionally opened activity item with the mailbox selection.
        if (this.viewingSelectedActivity) {
            return;
        }
        const ctx = this.messageContext;
        if (!ctx)
            return;
        const job = this.findActivityJob("email_scam_check", {
            clientMessageId: ctx.clientMessageId,
            messageId: ctx.messageId,
            senderEmail: ctx.senderEmail,
        });
        if (!job)
            return;
        const verdict = scamResultFromJob(job);
        if (verdict) {
            this.scamCheckResult = verdict;
            this.scamCheckError = null;
            this.focusedActivityJobId = job.id;
            this.viewingSelectedActivity = false;
        }
    }
    findFocusedActivityJob() {
        if (this.focusedActivityJobId == null) {
            return null;
        }
        const focusedId = Number(this.focusedActivityJobId);
        return (this.unsubscribeJobs.find((job) => Number(job.id) === focusedId) ?? null);
    }
    activityScamFilter() {
        if (!this.viewingSelectedActivity) {
            return undefined;
        }
        const job = this.findFocusedActivityJob();
        if (!job?.payload) {
            return undefined;
        }
        return {
            clientMessageId: job.payload.client_message_id,
            messageId: job.payload.message_id,
        };
    }
    async refreshUnsubscribeJobs() {
        if (!this.options.getUnsubscribeTargets && !this.options.getScamCheckTargets) {
            this.unsubscribeJobs = [];
            return;
        }
        try {
            const { jobs } = await listUnsubscribeJobs(this.options.storage, 20);
            this.unsubscribeJobs = jobs;
            this.hydrateScamResultFromJobs();
        }
        catch {
            // Keep the last known list if the status endpoint is unavailable.
        }
    }
    unsubscribeStatusLabel(job) {
        // Prefer the tool result: EmailUnsubscribe writes result JSON while the
        // dispatcher row may still be "processing" for a short window.
        const resultStatus = job.result?.unsubscribe_status;
        if (resultStatus) {
            return resultStatus;
        }
        if (job.status === "exhausted") {
            return "failed";
        }
        if (job.status === "pending" || job.status === "processing") {
            return job.status;
        }
        return job.status;
    }
    humanizeUnsubscribeAction(action) {
        const raw = (action || "").trim();
        if (!raw)
            return "";
        if (raw === "one_click_post")
            return "one-click POST";
        if (raw === "fill_email")
            return "filled email";
        if (raw === "verify_via_reload")
            return "verified after reload";
        if (raw === "verify_via_reload:toggles_off") {
            return "verified toggles off";
        }
        const clickText = /^click_text:(.+)$/i.exec(raw);
        if (clickText)
            return `clicked “${clickText[1].slice(0, 40)}”`;
        const uncheck = /^uncheck_category:(.+)$/i.exec(raw);
        if (uncheck)
            return `unchecked “${uncheck[1].slice(0, 40)}”`;
        if (/^click_selector/i.test(raw))
            return "clicked control";
        if (/^click_coords/i.test(raw))
            return "clicked page";
        return raw.replace(/_/g, " ");
    }
    unsubscribeActivityDetail(job) {
        const result = job.result;
        const status = result?.unsubscribe_status;
        const error = result?.unsubscribe_error ||
            (job.status === "exhausted" ? job.last_error : null) ||
            "";
        const actions = (result?.actions || [])
            .filter((a) => typeof a === "string" && a.trim().length > 0)
            .map((a) => this.humanizeUnsubscribeAction(a))
            .filter(Boolean);
        const match = typeof result?.success_match === "string"
            ? result.success_match.trim()
            : "";
        const parts = [];
        if (actions.length) {
            const trail = actions.slice(0, 4).join(" → ");
            parts.push(actions[0] === "one-click POST" && actions.length === 1
                ? "via one-click POST"
                : `via ${trail}`);
        }
        else if (status === "needs_mailto") {
            parts.push("mailto fallback");
        }
        else if (status === "emailed") {
            parts.push("mailto opened");
        }
        else if (status === "successful") {
            parts.push("unsubscribe completed");
        }
        if (match && match !== "llm:confirmed") {
            const snippet = match.length > 60 ? `${match.slice(0, 57)}…` : match;
            parts.push(`confirmed: ${snippet}`);
        }
        else if (match === "llm:confirmed") {
            parts.push("LLM confirmed success");
        }
        if (error && status !== "successful" && status !== "emailed") {
            parts.push(error);
        }
        const detail = parts.join(" · ");
        if (detail.length <= 140)
            return detail;
        return `${detail.slice(0, 137)}…`;
    }
    upsertUnsubscribeJob(job) {
        const idx = this.unsubscribeJobs.findIndex((j) => j.id === job.id);
        if (idx >= 0) {
            this.unsubscribeJobs[idx] = job;
            return;
        }
        this.unsubscribeJobs = [job, ...this.unsubscribeJobs].slice(0, 10);
    }
    formatUnsubscribeStatus(status) {
        const raw = (status || "").trim().toLowerCase();
        const labels = {
            successful: "Successful",
            failed: "Failed",
            processing: "Processing",
            pending: "Processing",
            exhausted: "Failed",
            needs_mailto: "Needs Mailto",
            emailed: "Emailed",
            completed: "Completed",
        };
        const label = labels[raw] ||
            raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
            "Unknown";
        if (raw === "successful") {
            return { label, tone: "successful" };
        }
        if (raw === "failed" || raw === "exhausted") {
            return { label, tone: "failed" };
        }
        if (raw === "processing" || raw === "pending") {
            return { label, tone: "processing" };
        }
        return { label, tone: "other" };
    }
    async applySettings(settings, message) {
        this.settings = settings;
        if (this.accountName) {
            await this.options.settingsCache?.save(this.accountName, settings);
        }
        this.renderDashboard();
        if (message) {
            flash(this.options.root, message.text, message.type);
        }
    }
    async confirmRemoveFromList(count, listName) {
        const noun = count === 1 ? "item" : "items";
        return showConfirmDialog(this.options.root, {
            title: "Remove selected?",
            message: `Remove ${count} ${noun} from ${listName}?`,
            confirmLabel: "Remove selected",
            cancelLabel: "Cancel",
            danger: true,
        });
    }
    async removeListItems(action, items, listName) {
        if (items.length === 0) {
            return;
        }
        const confirmed = await this.confirmRemoveFromList(items.length, listName);
        if (!confirmed) {
            return;
        }
        try {
            let settings = this.settings;
            for (const item of items) {
                settings = await updateList(this.options.storage, action, item, "remove");
            }
            const noun = items.length === 1 ? "item" : "items";
            await this.applySettings(settings, {
                text: `Removed ${items.length} ${noun} from ${listName}.`,
                type: "success",
            });
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Remove failed", "error");
        }
    }
    async removeCustomScoreRules(rules) {
        if (rules.length === 0) {
            return;
        }
        const confirmed = await this.confirmRemoveFromList(rules.length, "custom rule scores");
        if (!confirmed) {
            return;
        }
        try {
            let settings = this.settings;
            for (const rule of rules) {
                settings = await updateCustomScore(this.options.storage, rule, "", "remove");
            }
            const noun = rules.length === 1 ? "rule" : "rules";
            await this.applySettings(settings, {
                text: `Removed ${rules.length} custom score ${noun}.`,
                type: "success",
            });
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Remove failed", "error");
        }
    }
    async removeMailboxAliases(aliases) {
        const account = this.accountName;
        if (!account || aliases.length === 0) {
            return;
        }
        const confirmed = await this.confirmRemoveFromList(aliases.length, "mailbox addresses");
        if (!confirmed) {
            return;
        }
        try {
            for (const alias of aliases) {
                await this.options.storage.removeMailboxAlias(account, alias);
            }
            await this.loadAccountAliases();
            this.manualAccountOverride = false;
            await this.autoSelectAccount();
            if (this.showingMailboxAddresses) {
                this.renderMailboxAddressesView();
            }
            else {
                await this.loadDashboard();
            }
            const noun = aliases.length === 1 ? "address" : "addresses";
            flash(this.options.root, `Removed ${aliases.length} mailbox ${noun}.`, "success");
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Remove failed", "error");
        }
    }
    renderAccountUserBar() {
        const userBar = el("div", "topbar-user");
        const account = this.accountName ?? "";
        const accountRow = el("div", "topbar-user-account");
        if (this.options.getMessageContext) {
            const addressesLink = el("button", "btn btn-link", "Addresses");
            addressesLink.type = "button";
            addressesLink.title = "Mailbox addresses for this account";
            addressesLink.addEventListener("click", () => {
                this.renderMailboxAddressesView();
            });
            accountRow.append(addressesLink);
        }
        if (this.pairedAccounts.length >= 2) {
            const select = document.createElement("select");
            select.className = "account-select";
            select.title = "Active account";
            for (const paired of this.pairedAccounts) {
                const option = document.createElement("option");
                option.value = paired;
                option.textContent = paired;
                option.selected = paired === account;
                select.append(option);
            }
            select.addEventListener("change", () => {
                void this.switchAccount(select.value);
            });
            accountRow.append(select);
        }
        else {
            accountRow.append(el("span", undefined, account));
        }
        userBar.append(accountRow);
        const actions = el("div", "topbar-user-actions");
        const openFull = this.renderOpenFullPageButton();
        if (openFull) {
            actions.append(openFull);
        }
        const addAccount = el("button", "btn btn-link", "Add account");
        addAccount.type = "button";
        addAccount.addEventListener("click", () => this.renderAddAccount());
        const removeAccount = el("button", "btn btn-link", "Remove account");
        removeAccount.type = "button";
        removeAccount.addEventListener("click", () => {
            void this.confirmRemoveActiveAccount();
        });
        actions.append(addAccount, removeAccount);
        userBar.append(actions);
        return userBar;
    }
    renderMismatchBanner() {
        const candidates = this.mailboxCandidates();
        if (candidates.length === 0 ||
            this.matchingMailboxAccount() ||
            this.isMailboxIdentityIgnored()) {
            return null;
        }
        const label = this.messageContext?.mailboxLabel ??
            this.messageContext?.mailboxAccount ??
            candidates[0];
        const banner = el("div", "flash flash-error");
        banner.append(el("p", "hint", `Reading mail for ${label} — pair, switch account, or add a mailbox address so this account can be recognized.`));
        const actions = el("div", "inline-add");
        const mailboxEmail = this.primaryMailboxEmail();
        if (mailboxEmail && this.accountName) {
            const addBtn = el("button", "btn btn-secondary btn-sm", `Add ${mailboxEmail} to this account`);
            addBtn.type = "button";
            addBtn.addEventListener("click", () => {
                void this.addMailboxAlias(mailboxEmail);
            });
            actions.append(addBtn);
        }
        const ignoreBtn = el("button", "btn btn-secondary btn-sm", "Ignore this mailbox");
        ignoreBtn.type = "button";
        ignoreBtn.title = "Hide this warning for mailboxes that are not on these servers";
        ignoreBtn.addEventListener("click", () => {
            void this.ignoreMailboxIdentity(label);
        });
        actions.append(ignoreBtn);
        banner.append(actions);
        return banner;
    }
    async ignoreMailboxIdentity(identity) {
        try {
            await this.options.storage.addIgnoredMailboxIdentity(identity);
            await this.loadIgnoredMailboxIdentities();
            if (this.showingMailboxAddresses) {
                this.renderMailboxAddressesView();
            }
            else {
                this.renderDashboard();
            }
            flash(this.options.root, `Ignoring mailbox ${identity}.`, "success");
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Ignore failed", "error");
        }
    }
    async removeIgnoredMailboxIdentities(identities) {
        if (identities.length === 0) {
            return;
        }
        try {
            for (const identity of identities) {
                await this.options.storage.removeIgnoredMailboxIdentity(identity);
            }
            await this.loadIgnoredMailboxIdentities();
            if (this.showingMailboxAddresses) {
                this.renderMailboxAddressesView();
            }
            else {
                this.renderDashboard();
            }
            const noun = identities.length === 1 ? "mailbox" : "mailboxes";
            flash(this.options.root, `Stopped ignoring ${identities.length} ${noun}.`, "success");
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Update failed", "error");
        }
    }
    async addMailboxAlias(alias) {
        const account = this.accountName;
        if (!account) {
            return;
        }
        try {
            await this.options.storage.addMailboxAlias(account, alias);
            await this.loadAccountAliases();
            this.manualAccountOverride = false;
            await this.autoSelectAccount();
            if (this.showingMailboxAddresses) {
                this.renderMailboxAddressesView();
            }
            else {
                await this.loadDashboard();
            }
            flash(this.options.root, `${alias} added to mailbox addresses.`, "success");
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Add failed", "error");
        }
    }
    mailboxMatchStatusLines(account) {
        const candidates = this.mailboxCandidates();
        const primary = this.primaryMailboxEmail() ??
            this.messageContext?.mailboxLabel ??
            this.messageContext?.mailboxAccount ??
            candidates[0] ??
            null;
        const aliases = this.accountAliases[account] ?? [];
        const matchedAccount = this.matchingMailboxAccount();
        if (!this.options.getMessageContext) {
            return [
                "Mailbox matching is available when this app is opened from your mail client.",
            ];
        }
        if (candidates.length === 0) {
            return [
                "No mailbox identity is available from the current mail client context yet.",
            ];
        }
        const lines = [
            `Mail client mailbox identity: ${primary ?? candidates.join(", ")}`,
        ];
        if (matchedAccount && accountsMatch(matchedAccount, account)) {
            const matchedViaAlias = aliases.find((alias) => candidates.some((candidate) => accountsMatch(candidate, alias)));
            const matchedViaUsername = candidates.some((candidate) => accountsMatch(candidate, account));
            if (matchedViaUsername) {
                lines.push(`Currently matching this paired account via the IMAP username (${account}).`);
            }
            else if (matchedViaAlias) {
                lines.push(`Currently matching this paired account via mailbox address ${matchedViaAlias}.`);
            }
            else {
                lines.push("Currently matching this paired account.");
            }
        }
        else if (matchedAccount) {
            lines.push(`Currently matching a different paired account (${matchedAccount}). Switch accounts or add an address below.`);
        }
        else {
            lines.push("Not currently matching any paired account. Add the mailbox identity as an address below if it belongs to this account.");
        }
        return lines;
    }
    renderMailboxAddressesView() {
        const { root } = this.options;
        this.showingMailboxAddresses = true;
        this.lockDashboardHeight();
        root.replaceChildren();
        const header = this.renderHeader(this.renderAccountUserBar());
        const main = el("main", "container");
        const section = el("section", "card");
        section.append(el("h2", undefined, "Mailbox addresses"));
        if (!this.accountName) {
            section.append(el("p", "muted", "Pair an account to manage mailbox addresses."));
        }
        else if (!this.options.getMessageContext) {
            section.append(el("p", "muted", "Mailbox addresses are available when this app is opened from your mail client."));
        }
        else {
            const account = this.accountName;
            const aliases = this.accountAliases[account] ?? [];
            section.append(el("p", "hint", "Use mailbox addresses when your mail client's mailbox identity (the account or folder you are reading) does not match the IMAP username used to pair this device. Extra addresses let this app recognize which paired account owns the current mailbox so preferences and filters apply to the right account."));
            section.append(el("p", "hint", `Paired account: ${account}`));
            for (const line of this.mailboxMatchStatusLines(account)) {
                section.append(el("p", "hint", line));
            }
            if (aliases.length === 0) {
                section.append(el("p", "muted", "No extra mailbox addresses added."));
            }
            else {
                const list = el("ul", "address-list");
                for (const alias of aliases) {
                    list.append(createSelectableListItem(alias));
                }
                section.append(list);
                const toolbar = el("div", "list-toolbar");
                const removeSelected = el("button", "btn btn-danger btn-sm", "Remove selected");
                removeSelected.type = "button";
                removeSelected.disabled = true;
                removeSelected.addEventListener("click", () => {
                    const selected = selectedCheckboxValues(list, ".list-select-checkbox");
                    void this.removeMailboxAliases(selected);
                });
                wireRemoveSelectedButton(list, ".list-select-checkbox", removeSelected);
                toolbar.append(createToggleAllButton(list, ".list-select-checkbox"), removeSelected);
                section.append(toolbar);
            }
            const addRow = el("div", "inline-add mt");
            const input = document.createElement("input");
            input.type = "email";
            input.placeholder = "you@example.com";
            input.required = true;
            const addBtn = el("button", "btn btn-secondary btn-sm", "Add address");
            addBtn.type = "button";
            addBtn.addEventListener("click", () => {
                const value = input.value.trim();
                if (!value) {
                    return;
                }
                void this.addMailboxAlias(value);
            });
            addRow.append(input, addBtn);
            section.append(addRow);
            section.append(el("h3", "mt", "Ignored mailboxes"));
            section.append(el("p", "hint", "Ignored mailboxes hide the unrecognized-mailbox warning. Use this for accounts that are not on these servers."));
            const ignored = this.ignoredMailboxIdentities;
            if (ignored.length === 0) {
                section.append(el("p", "muted", "No ignored mailboxes."));
            }
            else {
                const ignoredList = el("ul", "address-list");
                for (const identity of ignored) {
                    ignoredList.append(createSelectableListItem(identity));
                }
                section.append(ignoredList);
                const ignoredToolbar = el("div", "list-toolbar");
                const stopIgnoring = el("button", "btn btn-danger btn-sm", "Stop ignoring selected");
                stopIgnoring.type = "button";
                stopIgnoring.disabled = true;
                stopIgnoring.addEventListener("click", () => {
                    const selected = selectedCheckboxValues(ignoredList, ".list-select-checkbox");
                    void this.removeIgnoredMailboxIdentities(selected);
                });
                wireRemoveSelectedButton(ignoredList, ".list-select-checkbox", stopIgnoring);
                ignoredToolbar.append(createToggleAllButton(ignoredList, ".list-select-checkbox"), stopIgnoring);
                section.append(ignoredToolbar);
            }
        }
        const actions = el("div", "inline-add mt");
        const back = el("button", "btn btn-secondary", "Back");
        back.type = "button";
        back.addEventListener("click", () => {
            this.showingMailboxAddresses = false;
            void this.loadDashboard();
        });
        actions.append(back);
        section.append(actions);
        main.append(section);
        root.append(header, main);
        this.lockDashboardHeight();
        requestAnimationFrame(() => this.lockDashboardHeight());
    }
    async removeMailboxAlias(alias) {
        await this.removeMailboxAliases([alias]);
    }
    hasCurrentMessage() {
        const ctx = this.messageContext;
        if (!ctx) {
            return false;
        }
        if (ctx.senderEmail || (ctx.senderEmails?.length ?? 0) > 0) {
            return true;
        }
        return (ctx.selectionCount ?? 0) > 0;
    }
    renderSectionNav() {
        const nav = el("nav", "section-nav");
        for (const section of APP_SECTIONS) {
            const btn = el("button", `btn btn-secondary btn-sm${this.activeSection === section.id ? " active" : ""}`, section.label);
            btn.type = "button";
            btn.disabled = !section.enabled;
            btn.addEventListener("click", () => {
                if (this.activeSection === section.id) {
                    return;
                }
                this.activeSection = section.id;
                this.renderDashboard();
            });
            nav.append(btn);
        }
        return nav;
    }
    renderDashboard() {
        if (this.showingMailboxAddresses) {
            this.renderMailboxAddressesView();
            return;
        }
        const { root } = this.options;
        // Capture height before replacing content so the popup does not shrink
        // when switching to a shorter section (e.g. Spam Filter → Server Filters).
        this.lockDashboardHeight();
        root.replaceChildren();
        const header = this.renderHeader(this.renderAccountUserBar());
        const main = el("main", "container");
        const mismatch = this.renderMismatchBanner();
        if (mismatch) {
            main.append(mismatch);
        }
        // Keep Current Message / Selected activity above section tabs, and across
        // section switches, when a message is selected or an activity is open.
        if (this.hasCurrentMessage() || this.viewingSelectedActivity) {
            main.append(this.renderMessageActions());
        }
        const unsubStatus = this.renderUnsubscribeStatus();
        if (unsubStatus) {
            main.append(unsubStatus);
        }
        main.append(this.renderSectionNav());
        if (this.activeSection === "sieve") {
            main.append(renderSieveSection({ storage: this.options.storage, root: this.options.root }));
            root.append(header, main);
            this.lockDashboardHeight();
            requestAnimationFrame(() => this.lockDashboardHeight());
            return;
        }
        if (this.activeSection === "archiving") {
            main.append(renderArchivingSection({
                storage: this.options.storage,
                root: this.options.root,
                getArchiveFolderContext: this.options.getArchiveFolderContext,
                countArchiveMessages: this.options.countArchiveMessages,
                runArchive: this.options.runArchive,
            }));
            root.append(header, main);
            this.lockDashboardHeight();
            requestAnimationFrame(() => this.lockDashboardHeight());
            return;
        }
        if (this.mailServerType === "kerio") {
            main.append(renderKerioSpamSection({ storage: this.options.storage }));
            main.append(renderKerioDavHelperSection({ storage: this.options.storage }));
            root.append(header, main);
            this.lockDashboardHeight();
            requestAnimationFrame(() => this.lockDashboardHeight());
            return;
        }
        main.append(this.renderPreferences());
        main.append(this.renderListSection("Allowed Senders", "whitelist_from", "whitelist", "email", undefined, "No allowed senders."));
        main.append(this.renderListSection("Denied Senders", "blacklist_from", "blacklist", "email", DENY_LIST_HINTS, "No denied senders.", DENY_LIST_LIMIT));
        main.append(this.renderListSection("Allowed Subjects", "whitelist_subject", "whitelist_subject", "text", "Subject lines that should be allowed (glob patterns supported).", "No allowed subjects."));
        main.append(this.renderListSection("Denied Subjects", "blacklist_subject", "blacklist_subject", "text", "Subject lines that should be denied (glob patterns supported).", "No denied subjects."));
        main.append(this.renderCustomScores());
        root.append(header, main);
        this.lockDashboardHeight();
        requestAnimationFrame(() => this.lockDashboardHeight());
    }
    renderMessageActions() {
        const focusedJob = this.viewingSelectedActivity
            ? this.findFocusedActivityJob() ?? undefined
            : undefined;
        const email = this.viewingSelectedActivity
            ? focusedJob?.payload?.sender_email || undefined
            : this.messageContext?.senderEmail;
        const senderEmails = this.viewingSelectedActivity
            ? email
                ? [email]
                : []
            : this.messageContext?.senderEmails?.filter(Boolean) ??
                (email ? [email] : []);
        const selectionCount = this.viewingSelectedActivity
            ? senderEmails.length || 1
            : this.messageContext?.selectionCount ??
                (senderEmails.length > 0 ? senderEmails.length : email ? 1 : 0);
        const subject = this.viewingSelectedActivity
            ? focusedJob?.payload?.subject?.trim() || "(no subject)"
            : this.messageContext?.subject?.trim() || "(no subject)";
        const section = el("section", "card");
        section.append(el("h2", undefined, this.viewingSelectedActivity ? "Selected activity" : "Current Message"));
        if (senderEmails.length > 1) {
            const identity = el("div", "current-message-identity");
            identity.append(el("div", "muted", "Senders:"));
            const senders = el("ul", "current-message-senders");
            for (const sender of senderEmails) {
                const item = document.createElement("li");
                item.append(el("div", "unsubscribe-status-sender", sender));
                senders.append(item);
            }
            identity.append(senders);
            identity.append(el("div", "unsubscribe-status-subject muted", subject));
            section.append(identity);
        }
        else if (senderEmails.length === 1) {
            const identity = el("div", "current-message-identity");
            identity.append(el("div", "unsubscribe-status-sender", senderEmails[0]));
            identity.append(el("div", "unsubscribe-status-subject muted", subject));
            section.append(identity);
        }
        else {
            const identity = el("div", "current-message-identity");
            identity.append(el("div", "unsubscribe-status-sender", selectionCount === 1
                ? "1 message selected"
                : `${selectionCount} messages selected`));
            identity.append(el("div", "unsubscribe-status-subject muted", subject));
            section.append(identity);
        }
        if (this.options.getUnsubscribeTargets || this.options.getScamCheckTargets) {
            const actionRow = el("div", "inline-add mt message-action-row");
            const messageIds = this.viewingSelectedActivity
                ? {
                    clientMessageId: focusedJob?.payload?.client_message_id,
                    messageId: focusedJob?.payload?.message_id,
                    senderEmail: focusedJob?.payload?.sender_email || undefined,
                }
                : {
                    clientMessageId: this.messageContext?.clientMessageId,
                    messageId: this.messageContext?.messageId,
                    senderEmail: this.messageContext?.senderEmail,
                };
            if (this.options.getUnsubscribeTargets && !this.viewingSelectedActivity) {
                const existingUnsub = this.findActivityJob("email_unsubscribe", messageIds);
                const unsubBtn = el("button", "btn btn-secondary btn-sm", existingUnsub ? "View unsubscribe" : "Unsubscribe");
                unsubBtn.type = "button";
                unsubBtn.addEventListener("click", () => {
                    void this.runUnsubscribe(unsubBtn);
                });
                actionRow.append(unsubBtn);
            }
            if (this.options.getScamCheckTargets && !this.viewingSelectedActivity) {
                const existingScam = this.findActivityJob("email_scam_check", messageIds);
                const scamBtn = el("button", "btn btn-secondary btn-sm", existingScam || this.scamCheckResult ? "View scam check" : "Check for scam");
                scamBtn.type = "button";
                scamBtn.title =
                    "Sends this message to Email Apps for analysis (advisory only)";
                scamBtn.addEventListener("click", () => {
                    this.viewingSelectedActivity = false;
                    void this.runScamCheck(scamBtn);
                });
                actionRow.append(scamBtn);
            }
            if (actionRow.childNodes.length) {
                section.append(actionRow);
            }
        }
        if (this.viewingSelectedActivity) {
            const focused = focusedJob ?? this.findFocusedActivityJob();
            if (focused?.job_type === "email_unsubscribe") {
                section.append(this.renderUnsubscribeResult(focused));
            }
            else if (this.scamCheckResult || this.scamCheckError) {
                section.append(this.renderScamCheckResult());
            }
            return section;
        }
        if (this.scamCheckResult || this.scamCheckError) {
            section.append(this.renderScamCheckResult());
        }
        // SpamAssassin allow/deny — Dovecot only.
        // Kerio uses the trusted-senders (spam whitelist) list instead.
        if (this.mailServerType === "kerio") {
            if (email && selectionCount <= 1) {
                section.append(this.renderKerioTrustedSenderActions(email));
            }
            return section;
        }
        // Allow/deny apply to a single sender; skip when multi-select.
        if (!email || selectionCount > 1) {
            return section;
        }
        const allowedSenders = parseListValues(this.settings.whitelist_from ?? "");
        const deniedSenders = parseListValues(this.settings.blacklist_from ?? "");
        const onAllowList = senderListIncludes(allowedSenders, email);
        const onDenyList = senderListIncludes(deniedSenders, email);
        const denyListFull = deniedSenders.length >= DENY_LIST_LIMIT;
        const row = el("div", "inline-add mt");
        if (onAllowList) {
            row.append(el("span", "muted", "Already on allow list"));
        }
        else if (onDenyList) {
            row.append(el("span", "muted", "Already on deny list"));
        }
        else {
            const allowBtn = el("button", "btn btn-secondary btn-sm", "Add to allow list");
            allowBtn.type = "button";
            allowBtn.addEventListener("click", async () => {
                try {
                    await this.applySettings(await updateList(this.options.storage, "whitelist", email, "add"), { text: `${email} added to allow list.`, type: "success" });
                }
                catch (error) {
                    flash(this.options.root, error instanceof Error ? error.message : "Add failed", "error");
                }
            });
            row.append(allowBtn);
            if (denyListFull) {
                row.append(el("span", "muted", `Deny list is full (${DENY_LIST_LIMIT}/${DENY_LIST_LIMIT}).`));
            }
            else {
                const denyBtn = el("button", "btn btn-danger btn-sm", "Add to deny list");
                denyBtn.type = "button";
                denyBtn.addEventListener("click", async () => {
                    try {
                        await this.applySettings(await updateList(this.options.storage, "blacklist", email, "add"), { text: `${email} added to deny list.`, type: "success" });
                    }
                    catch (error) {
                        flash(this.options.root, error instanceof Error ? error.message : "Add failed", "error");
                    }
                });
                row.append(denyBtn);
            }
        }
        section.append(row);
        return section;
    }
    renderKerioTrustedSenderActions(email) {
        const row = el("div", "inline-add mt");
        const status = el("span", "muted", "Checking trusted senders…");
        row.append(status);
        const normalized = email.trim().toLowerCase();
        const paintTrusted = () => {
            row.replaceChildren();
            row.append(el("span", "muted", "Already a trusted sender"));
        };
        const paintButton = () => {
            row.replaceChildren();
            const addBtn = el("button", "btn btn-secondary btn-sm", "Add to trusted senders");
            addBtn.type = "button";
            addBtn.addEventListener("click", () => {
                void (async () => {
                    addBtn.disabled = true;
                    try {
                        const settings = await getKerioSpamSettings(this.options.storage);
                        const already = settings.whiteList.some((entry) => entry.trim().toLowerCase() === normalized);
                        if (already) {
                            paintTrusted();
                            flash(this.options.root, `${email} is already a trusted sender.`, "info");
                            return;
                        }
                        await setKerioSpamSettings(this.options.storage, {
                            ...settings,
                            whiteList: [...settings.whiteList, normalized],
                        });
                        this.renderDashboard();
                        flash(this.options.root, `${email} added to trusted senders.`, "success");
                    }
                    catch (error) {
                        addBtn.disabled = false;
                        flash(this.options.root, error instanceof Error ? error.message : "Add failed", "error");
                    }
                })();
            });
            row.append(addBtn);
        };
        void (async () => {
            try {
                const settings = await getKerioSpamSettings(this.options.storage);
                const already = settings.whiteList.some((entry) => entry.trim().toLowerCase() === normalized);
                if (already) {
                    paintTrusted();
                }
                else {
                    paintButton();
                }
            }
            catch (error) {
                row.replaceChildren();
                row.append(el("span", "muted", error instanceof Error
                    ? error.message
                    : "Could not load trusted senders."));
            }
        })();
        return row;
    }
    renderUnsubscribeStatus() {
        if ((!this.options.getUnsubscribeTargets && !this.options.getScamCheckTargets) ||
            this.unsubscribeJobs.length === 0) {
            return null;
        }
        const section = el("section", "card unsubscribe-status");
        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "unsubscribe-status-toggle";
        toggle.setAttribute("aria-expanded", this.unsubscribeStatusExpanded ? "true" : "false");
        const chevron = el("span", "unsubscribe-status-chevron", "▸");
        chevron.setAttribute("aria-hidden", "true");
        const title = el("span", "unsubscribe-status-title", "Message activity");
        toggle.append(chevron, title);
        const list = el("ul", "unsubscribe-status-list");
        list.hidden = !this.unsubscribeStatusExpanded;
        if (this.unsubscribeStatusExpanded) {
            section.classList.add("is-open");
            chevron.textContent = "▾";
        }
        toggle.addEventListener("click", () => {
            this.unsubscribeStatusExpanded = !this.unsubscribeStatusExpanded;
            const open = this.unsubscribeStatusExpanded;
            toggle.setAttribute("aria-expanded", open ? "true" : "false");
            list.hidden = !open;
            section.classList.toggle("is-open", open);
            chevron.textContent = open ? "▾" : "▸";
        });
        for (const job of this.unsubscribeJobs.slice(0, 12)) {
            const isScam = job.job_type === "email_scam_check";
            const status = isScam
                ? job.result?.risk || job.status
                : this.unsubscribeStatusLabel(job);
            const { label, tone } = isScam
                ? this.formatScamStatus(status)
                : this.formatUnsubscribeStatus(status);
            const sender = job.payload?.sender_email || "Unknown sender";
            const subject = job.payload?.subject?.trim() || "(no subject)";
            const detail = isScam
                ? job.result?.summary || ""
                : this.unsubscribeActivityDetail(job);
            const item = document.createElement("li");
            if (this.focusedActivityJobId === job.id) {
                item.classList.add("is-focused");
            }
            const kind = el("div", "unsubscribe-status-kind muted", isScam ? "Scam check" : "Unsubscribe");
            item.append(kind);
            item.append(el("div", "unsubscribe-status-sender", sender));
            item.append(el("div", "unsubscribe-status-subject muted", subject));
            const state = el("div", "unsubscribe-status-state");
            state.append(el("span", `unsubscribe-status-value is-${tone}`, label));
            if (detail) {
                const clipped = detail.length > 140 ? `${detail.slice(0, 137)}…` : detail;
                state.append(el("span", "unsubscribe-status-detail muted", ` (${clipped})`));
            }
            item.append(state);
            if (isScam) {
                item.classList.add("is-mailto-action");
                item.setAttribute("role", "button");
                item.tabIndex = 0;
                item.title = "Show scam check result";
                const showScam = () => {
                    const verdict = scamResultFromJob(job);
                    if (!verdict)
                        return;
                    this.scamCheckResult = verdict;
                    this.scamCheckError = null;
                    this.scamFeedbackSubmittedLabel = null;
                    this.focusedActivityJobId = job.id;
                    this.viewingSelectedActivity = true;
                    this.unsubscribeStatusExpanded = true;
                    void this.refreshOpenDisagreementCount().then(() => {
                        this.renderDashboard();
                    });
                };
                item.addEventListener("click", showScam);
                item.addEventListener("keydown", (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        showScam();
                    }
                });
            }
            else {
                item.classList.add("is-mailto-action");
                item.setAttribute("role", "button");
                item.tabIndex = 0;
                item.title = "Show unsubscribe result";
                item.setAttribute("aria-label", `Show unsubscribe result for ${sender}`);
                const showUnsub = () => {
                    this.focusedActivityJobId = job.id;
                    this.viewingSelectedActivity = true;
                    this.unsubscribeStatusExpanded = true;
                    this.renderDashboard();
                };
                item.addEventListener("click", showUnsub);
                item.addEventListener("keydown", (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        showUnsub();
                    }
                });
            }
            list.append(item);
        }
        section.append(toggle, list);
        return section;
    }
    formatScamStatus(status) {
        const raw = (status || "").trim().toLowerCase();
        if (raw === "high") {
            return { label: "High risk", tone: "failed" };
        }
        if (raw === "medium") {
            return { label: "Medium risk", tone: "other" };
        }
        if (raw === "low") {
            return { label: "Low risk", tone: "successful" };
        }
        if (raw === "unknown") {
            return { label: "Uncertain", tone: "other" };
        }
        return this.formatUnsubscribeStatus(status);
    }
    renderUnsubscribeResult(job) {
        const wrap = el("div", "unsubscribe-result mt");
        const status = this.unsubscribeStatusLabel(job);
        const { label, tone } = this.formatUnsubscribeStatus(status);
        wrap.append(el("div", `unsubscribe-result-badge is-${tone}`, `Unsubscribe · ${label}`));
        const detail = this.unsubscribeActivityDetail(job);
        if (detail) {
            wrap.append(el("p", "unsubscribe-result-summary", detail));
        }
        const actions = (job.result?.actions || []).filter((a) => typeof a === "string" && a.trim().length > 0);
        if (actions.length) {
            wrap.append(el("div", "muted unsubscribe-result-label", "What ran"));
            const list = el("ul", "unsubscribe-result-list");
            for (const action of actions.slice(0, 12)) {
                list.append(el("li", undefined, this.humanizeUnsubscribeAction(action) || action));
            }
            wrap.append(list);
        }
        const match = typeof job.result?.success_match === "string"
            ? job.result.success_match.trim()
            : "";
        if (match) {
            wrap.append(el("div", "muted unsubscribe-result-label", "Confirmation"));
            wrap.append(el("p", "unsubscribe-result-match", match === "llm:confirmed"
                ? "The unsubscribe agent confirmed success on the page."
                : match));
        }
        const error = job.result?.unsubscribe_error ||
            (job.status === "exhausted" ? job.last_error : null);
        if (error && status !== "successful" && status !== "emailed") {
            wrap.append(el("div", "muted unsubscribe-result-label", "Details"));
            wrap.append(el("p", "unsubscribe-result-error", error));
        }
        wrap.append(el("p", "muted unsubscribe-result-disclaimer", "Unsubscribe follows List-Unsubscribe links from the message. Prefer one-click or browser completion when available; only send a mailto unsubscribe if you trust the address."));
        const mailto = this.resolveUnsubscribeMailto(job);
        const canMailto = Boolean(mailto && this.options.sendMailto);
        const needsMailto = status === "needs_mailto";
        const actionRow = el("div", "unsubscribe-result-actions mt");
        if (canMailto) {
            const mailtoBtn = el("button", "secondary", needsMailto ? "Open unsubscribe email" : "Open mailto again");
            mailtoBtn.type = "button";
            mailtoBtn.title = needsMailto
                ? "Compose the unsubscribe email this message requires"
                : "Open the unsubscribe mailto again";
            mailtoBtn.addEventListener("click", () => {
                void (async () => {
                    const previous = mailtoBtn.textContent;
                    mailtoBtn.disabled = true;
                    mailtoBtn.textContent = "Opening…";
                    try {
                        const sent = await this.openUnsubscribeMailto(job, {
                            markComplete: needsMailto,
                        });
                        await this.refreshUnsubscribeJobs();
                        // Keep the refreshed job focused in Selected activity.
                        const refreshed = this.unsubscribeJobs.find((j) => j.id === job.id) ?? job;
                        this.focusedActivityJobId = refreshed.id;
                        this.viewingSelectedActivity = true;
                        this.unsubscribeStatusExpanded = true;
                        this.renderDashboard();
                        flash(this.options.root, sent
                            ? needsMailto
                                ? "Unsubscribe email opened."
                                : "Mailto opened."
                            : "Could not open unsubscribe email.", sent ? "success" : "error");
                    }
                    finally {
                        mailtoBtn.disabled = false;
                        mailtoBtn.textContent = previous || "Open unsubscribe email";
                    }
                })();
            });
            actionRow.append(mailtoBtn);
        }
        else if (needsMailto) {
            actionRow.append(el("span", "muted", "A mailto unsubscribe is required, but this client cannot open compose."));
        }
        if (actionRow.childNodes.length) {
            wrap.append(actionRow);
        }
        return wrap;
    }
    renderScamCheckResult() {
        const wrap = el("div", "scam-check-result mt");
        if (this.scamCheckError) {
            wrap.append(el("p", "scam-check-error", this.scamCheckError), el("p", "muted scam-check-disclaimer", "This is an advisory check only — it can be wrong."));
            return wrap;
        }
        const result = this.scamCheckResult;
        if (!result) {
            return wrap;
        }
        const riskLabel = result.risk === "high"
            ? "High risk"
            : result.risk === "medium"
                ? "Medium risk"
                : result.risk === "low"
                    ? "Low risk"
                    : "Uncertain";
        const badge = el("div", `scam-check-badge is-${result.risk}`, `${riskLabel} · confidence ${result.confidence}`);
        wrap.append(badge);
        wrap.append(el("p", "scam-check-summary", result.summary));
        if (result.reasons.length) {
            wrap.append(el("div", "muted scam-check-label", "Why"));
            const reasons = el("ul", "scam-check-list");
            for (const reason of result.reasons.slice(0, 8)) {
                reasons.append(el("li", undefined, reason));
            }
            wrap.append(reasons);
        }
        if (result.suggestedActions.length) {
            wrap.append(el("div", "muted scam-check-label", "Suggested actions"));
            const actions = el("ul", "scam-check-list");
            for (const action of result.suggestedActions.slice(0, 5)) {
                actions.append(el("li", undefined, action));
            }
            wrap.append(actions);
        }
        wrap.append(el("p", "muted scam-check-disclaimer", "Advisory only — this message was sent to Email Apps for analysis and the result may be wrong. Do not treat it as a definitive verdict. Submitting Scam / Not scam feedback sends message content to improve similar future checks."));
        const actionRow = el("div", "scam-check-actions mt");
        const againBtn = el("button", "secondary", "Check again");
        againBtn.type = "button";
        againBtn.addEventListener("click", () => {
            void this.runScamCheck(againBtn, { force: true });
        });
        actionRow.append(againBtn);
        const betaFeedback = isScamCheckAllowed(this.accountName);
        if (betaFeedback) {
            if (this.scamFeedbackSubmittedLabel) {
                const thanks = el("span", "muted scam-check-feedback-thanks", this.scamFeedbackSubmittedLabel === "scam"
                    ? "Thanks — labeled Scam."
                    : "Thanks — labeled Not scam.");
                actionRow.append(thanks);
            }
            else {
                const scamBtn = el("button", "secondary", "Scam");
                scamBtn.type = "button";
                scamBtn.title = "Label this message as a scam for training";
                scamBtn.addEventListener("click", () => {
                    void this.submitScamFeedback(scamBtn, "scam");
                });
                const notScamBtn = el("button", "secondary", "Not scam");
                notScamBtn.type = "button";
                notScamBtn.title = "Label this message as not a scam for training";
                notScamBtn.addEventListener("click", () => {
                    void this.submitScamFeedback(notScamBtn, "not_scam");
                });
                actionRow.append(scamBtn, notScamBtn);
            }
        }
        wrap.append(actionRow);
        if (betaFeedback && this.openDisagreementCount > 0) {
            const fixBox = el("div", "scam-check-fix-prompt mt");
            fixBox.append(el("p", "muted scam-check-fix-copy", `Feedback teaches similar messages via embeddings. Hard-coded heuristics and the system prompt still need a code change when labels disagree with the model. ${this.openDisagreementCount} open disagreement${this.openDisagreementCount === 1 ? "" : "s"} waiting — paste this prompt into Cursor (with apps-portal + apps-clients open).`));
            const copyBtn = el("button", "btn btn-secondary btn-sm", "Copy Cursor prompt");
            copyBtn.type = "button";
            copyBtn.addEventListener("click", async () => {
                try {
                    await navigator.clipboard.writeText(SCAM_HEURISTIC_FIX_PROMPT);
                    flash(this.options.root, "Cursor prompt copied.", "success");
                }
                catch {
                    flash(this.options.root, "Could not copy prompt to clipboard.", "error");
                }
            });
            fixBox.append(copyBtn);
            wrap.append(fixBox);
        }
        return wrap;
    }
    async submitScamFeedback(button, userLabel) {
        if (!this.options.getScamCheckTargets) {
            flash(this.options.root, "Scam feedback is not available in this client.", "error");
            return;
        }
        const jobId = this.scamCheckResult?.jobId;
        if (!jobId) {
            flash(this.options.root, "Run a scam check first (or Check again) before labeling.", "error");
            return;
        }
        const previous = button.textContent;
        button.disabled = true;
        button.textContent = "Saving…";
        try {
            const targets = await this.options.getScamCheckTargets(this.activityScamFilter());
            if (!targets.length) {
                flash(this.options.root, "No selected message found for feedback.", "error");
                return;
            }
            const saved = await submitScamCheckFeedback(this.options.storage, targets[0], {
                jobId,
                userLabel,
            });
            this.scamFeedbackSubmittedLabel = saved.userLabel;
            this.openDisagreementCount = saved.openDisagreementCount;
            flash(this.options.root, "Thanks — feedback saved.", "success");
            this.renderDashboard();
        }
        catch (error) {
            flash(this.options.root, error instanceof Error ? error.message : "Failed to save feedback.", "error");
        }
        finally {
            button.disabled = false;
            button.textContent = previous || (userLabel === "scam" ? "Scam" : "Not scam");
        }
    }
    async runScamCheck(button, options) {
        if (!this.options.getScamCheckTargets) {
            flash(this.options.root, "Scam check is not available in this client.", "error");
            return;
        }
        const force = options?.force === true;
        const previous = button.textContent;
        button.disabled = true;
        button.textContent = force ? "Re-checking…" : "Checking…";
        this.scamCheckError = null;
        const activityFilter = this.activityScamFilter();
        const keepActivityView = Boolean(activityFilter);
        if (!keepActivityView) {
            this.viewingSelectedActivity = false;
        }
        try {
            if (!force && !keepActivityView) {
                const ctxIds = {
                    clientMessageId: this.messageContext?.clientMessageId,
                    messageId: this.messageContext?.messageId,
                    senderEmail: this.messageContext?.senderEmail,
                };
                const localJob = this.findActivityJob("email_scam_check", ctxIds);
                if (localJob) {
                    const localVerdict = scamResultFromJob(localJob);
                    if (localVerdict) {
                        this.scamCheckResult = localVerdict;
                        this.scamFeedbackSubmittedLabel = null;
                        this.focusedActivityJobId = localJob.id;
                        this.viewingSelectedActivity = false;
                        this.unsubscribeStatusExpanded = true;
                        flash(this.options.root, `Showing previous scam check: ${localVerdict.risk} risk`, "info");
                        this.renderDashboard();
                        return;
                    }
                }
            }
            flash(this.options.root, force
                ? keepActivityView
                    ? "Re-analyzing the activity message with Email Apps…"
                    : "Re-analyzing selected message with Email Apps…"
                : "Reading selected message and sending it to Email Apps for analysis…", "info");
            const targets = await this.options.getScamCheckTargets(activityFilter);
            if (!targets.length) {
                this.scamCheckError = "No selected message found to analyze.";
                flash(this.options.root, this.scamCheckError, "error");
                this.renderDashboard();
                return;
            }
            const target = targets[0];
            if (!force) {
                const existing = this.findActivityJob("email_scam_check", {
                    clientMessageId: target.clientMessageId,
                    messageId: target.messageId,
                    senderEmail: target.senderEmail,
                });
                if (existing) {
                    const existingVerdict = scamResultFromJob(existing);
                    if (existingVerdict) {
                        this.scamCheckResult = existingVerdict;
                        this.scamFeedbackSubmittedLabel = null;
                        this.focusedActivityJobId = existing.id;
                        this.viewingSelectedActivity = keepActivityView;
                        this.unsubscribeStatusExpanded = true;
                        flash(this.options.root, `Showing previous scam check: ${existingVerdict.risk} risk`, "info");
                        this.renderDashboard();
                        return;
                    }
                }
            }
            const verdict = await checkMessageScam(this.options.storage, target, {
                force,
            });
            this.scamCheckResult = verdict;
            this.scamFeedbackSubmittedLabel = null;
            this.openDisagreementCount = verdict.openDisagreementCount ?? 0;
            this.focusedActivityJobId = verdict.jobId ?? null;
            this.viewingSelectedActivity = keepActivityView;
            this.unsubscribeStatusExpanded = true;
            await this.refreshUnsubscribeJobs();
            if (verdict.jobId) {
                this.focusedActivityJobId = verdict.jobId;
            }
            const flashType = verdict.risk === "high"
                ? "error"
                : verdict.risk === "medium"
                    ? "info"
                    : "success";
            flash(this.options.root, verdict.cached
                ? `Previous scam check: ${verdict.risk} risk — ${verdict.summary}`
                : `Scam check: ${verdict.risk} risk — ${verdict.summary}`, flashType);
            this.renderDashboard();
        }
        catch (error) {
            this.scamCheckError =
                error instanceof Error ? error.message : "Scam check failed.";
            flash(this.options.root, this.scamCheckError, "error");
            this.renderDashboard();
        }
        finally {
            button.disabled = false;
            button.textContent = previous || (force ? "Check again" : "Check for scam");
        }
    }
    async runUnsubscribe(button) {
        if (!this.options.getUnsubscribeTargets) {
            flash(this.options.root, "Unsubscribe is not available in this client.", "error");
            return;
        }
        const previous = button.textContent;
        button.disabled = true;
        button.textContent = "Working…";
        flash(this.options.root, "Reading selected message…", "info");
        try {
            const targets = await this.options.getUnsubscribeTargets();
            if (!targets.length) {
                flash(this.options.root, "No selected message found to unsubscribe.", "error");
                return;
            }
            const existingJobs = targets
                .map((target) => this.findActivityJob("email_unsubscribe", {
                clientMessageId: target.clientMessageId,
                messageId: target.messageId,
                senderEmail: target.senderEmail,
            }))
                .filter((job) => Boolean(job));
            if (existingJobs.length === targets.length) {
                for (const job of existingJobs) {
                    this.upsertUnsubscribeJob(job);
                }
                this.focusedActivityJobId = existingJobs[0]?.id ?? null;
                this.viewingSelectedActivity = true;
                this.unsubscribeStatusExpanded = true;
                const summaries = existingJobs.map((job) => {
                    const status = this.unsubscribeStatusLabel(job);
                    const { label } = this.formatUnsubscribeStatus(status);
                    const sender = job.payload?.sender_email || "message";
                    return `${sender}: ${label}`;
                });
                flash(this.options.root, `Showing previous unsubscribe result${existingJobs.length === 1 ? "" : "s"}: ${summaries.join("; ")}`, "info");
                this.renderDashboard();
                return;
            }
            flash(this.options.root, `Sending ${targets.length} unsubscribe request${targets.length === 1 ? "" : "s"}…`, "info");
            const { jobs, skipped } = await createUnsubscribeJobs(this.options.storage, targets);
            const summaries = [];
            for (const miss of skipped ?? []) {
                const label = miss.sender_email ||
                    miss.subject?.slice(0, 40) ||
                    miss.client_message_id;
                summaries.push(`skipped: ${label} (no unsubscribe link)`);
            }
            const pendingJobs = jobs.filter((job) => job.status === "pending" || job.status === "processing");
            const alreadyDone = jobs.filter((job) => job.status !== "pending" && job.status !== "processing");
            for (const job of alreadyDone) {
                this.upsertUnsubscribeJob(job);
                summaries.push(await this.finishUnsubscribeJob(job));
            }
            if (pendingJobs.length) {
                flash(this.options.root, `Queued ${pendingJobs.length} unsubscribe job${pendingJobs.length === 1 ? "" : "s"}…`, "info");
            }
            const finishedJobs = [];
            for (const job of pendingJobs) {
                const finished = await this.pollUnsubscribeJob(job.id);
                finishedJobs.push(finished);
                this.upsertUnsubscribeJob(finished);
                summaries.push(await this.finishUnsubscribeJob(finished));
            }
            if (!summaries.length) {
                summaries.push("No unsubscribe work to do.");
            }
            const failed = summaries.some((s) => s.startsWith("failed:") || s.startsWith("skipped:"));
            const anyOk = summaries.some((s) => s.startsWith("ok:") ||
                s.startsWith("emailed:") ||
                s.startsWith("mailto opened:"));
            await this.refreshUnsubscribeJobs();
            // List refresh can race the worker’s status flip to "completed"; keep the
            // polled result so the status panel does not stick on Processing. Also
            // re-upsert create-returned jobs (including already-done reuse hits) so
            // they stay visible when older than the list limit.
            for (const finished of finishedJobs) {
                this.upsertUnsubscribeJob(finished);
            }
            for (const job of alreadyDone) {
                this.upsertUnsubscribeJob(job);
            }
            this.focusedActivityJobId = jobs[0]?.id ?? this.focusedActivityJobId;
            this.viewingSelectedActivity = true;
            this.unsubscribeStatusExpanded = true;
            this.renderDashboard();
            flash(this.options.root, summaries.join(" · "), failed && !anyOk ? "error" : failed ? "info" : "success");
        }
        catch (error) {
            console.error("Unsubscribe failed", error);
            await this.refreshUnsubscribeJobs();
            this.renderDashboard();
            flash(this.options.root, error instanceof Error ? error.message : "Unsubscribe failed", "error");
        }
        finally {
            button.disabled = false;
            button.textContent = previous || "Unsubscribe";
        }
    }
    async pollUnsubscribeJob(jobId) {
        const deadline = Date.now() + 10 * 60 * 1000;
        while (Date.now() < deadline) {
            const job = await getUnsubscribeJob(this.options.storage, jobId);
            if (job.status === "completed" || job.status === "exhausted") {
                return job;
            }
            const resultStatus = job.result?.unsubscribe_status;
            if (resultStatus === "successful" ||
                resultStatus === "needs_mailto" ||
                resultStatus === "emailed" ||
                resultStatus === "failed") {
                return job;
            }
            await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        throw new Error(`Unsubscribe job ${jobId} timed out`);
    }
    resolveUnsubscribeMailto(job) {
        return (job.result?.mailto_urls?.[0] || job.payload?.mailto_urls?.[0] || null);
    }
    async openUnsubscribeMailto(job, options = {}) {
        const mailto = this.resolveUnsubscribeMailto(job);
        if (!mailto || !this.options.sendMailto) {
            return false;
        }
        const sent = await this.options.sendMailto(mailto);
        if (options.markComplete) {
            try {
                await completeUnsubscribeMailto(this.options.storage, job.id, sent);
            }
            catch {
                // Ignore status patch errors; compose may still have opened.
            }
        }
        return sent;
    }
    async finishUnsubscribeJob(job) {
        const label = job.payload?.sender_email ||
            job.payload?.subject?.slice(0, 40) ||
            "Unknown sender";
        if (job.status === "exhausted") {
            return `failed: ${label} (${job.last_error || "retries exhausted"})`;
        }
        const status = job.result?.unsubscribe_status;
        if (status === "successful") {
            return `ok: ${label}`;
        }
        if (status === "emailed") {
            return `emailed: ${label}`;
        }
        if (status === "needs_mailto") {
            if (!this.resolveUnsubscribeMailto(job) || !this.options.sendMailto) {
                return `failed: ${label} (mailto required but unavailable)`;
            }
            const sent = await this.openUnsubscribeMailto(job, { markComplete: true });
            return sent
                ? `mailto opened: ${label}`
                : `failed: ${label} (could not open mailto)`;
        }
        return `failed: ${label} (${job.result?.unsubscribe_error || job.last_error || "unknown"})`;
    }
    renderPreferences() {
        const section = el("section", "card");
        section.append(el("h2", undefined, "Spam Filter"));
        const form = el("form");
        const grid = el("div", "form-grid");
        const scoreLabel = el("label", undefined, "Required Score");
        scoreLabel.htmlFor = "required_score";
        const scoreSelect = document.createElement("select");
        scoreSelect.id = "required_score";
        scoreSelect.name = "required_score";
        for (const score of scoreOptions(this.settings.required_score)) {
            const option = document.createElement("option");
            option.value = score;
            option.textContent = score;
            if (score === (this.settings.required_score ?? "")) {
                option.selected = true;
            }
            scoreSelect.append(option);
        }
        grid.append(scoreLabel, scoreSelect);
        const toggles = [
            [
                "use_bayes",
                "Use Bayes filtering",
                "Learn from your spam and ham to improve scoring for this mailbox.",
            ],
            [
                "skip_rbl_checks",
                "Skip RBL checks",
                "When checked, skip realtime DNS blacklist (RBL) lookups. Leave unchecked to use RBL scoring.",
            ],
            [
                "use_razor2",
                "Use Razor2",
                "Query the Razor2 collaborative spam signature network.",
            ],
            [
                "use_pyzor",
                "Use Pyzor",
                "Query the Pyzor collaborative spam checksum network.",
            ],
            [
                "use_dcc",
                "Use DCC",
                "Query the Distributed Checksum Clearinghouse (DCC) network.",
            ],
        ];
        for (const [key, labelText, tooltip] of toggles) {
            const label = el("label", "checkbox-label");
            label.title = tooltip;
            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.name = key;
            checkbox.value = "1";
            checkbox.checked = (this.settings[key] ?? "0") === "1";
            checkbox.title = tooltip;
            label.append(checkbox, document.createTextNode(labelText));
            grid.append(label);
        }
        const langLabel = el("label", undefined, "OK Locales");
        langLabel.htmlFor = "ok_locales";
        langLabel.title =
            "Which character-set families are allowed. Other locales may score as foreign-language spam.";
        const langRow = el("div", "inline-add");
        const langInput = document.createElement("input");
        langInput.type = "text";
        langInput.id = "ok_locales";
        langInput.name = "ok_locales";
        langInput.value = this.settings.ok_locales ?? "en";
        langInput.placeholder = "en ja zh";
        langInput.title = langLabel.title;
        const pickLocales = el("button", "btn btn-secondary btn-sm", "Select…");
        pickLocales.type = "button";
        pickLocales.title = "Choose allowed locales from a list";
        pickLocales.addEventListener("click", async () => {
            const next = await showOkLocalesPicker(this.options.root, langInput.value);
            if (next !== null) {
                langInput.value = next;
            }
        });
        langRow.append(langInput, pickLocales);
        grid.append(langLabel, langRow, el("p", "hint", "Space-separated locale codes: all, en, ja, ko, ru, th, zh"));
        form.append(grid);
        const save = el("button", "btn btn-primary mt", "Save Preferences");
        save.type = "submit";
        form.append(save);
        form.addEventListener("submit", async (event) => {
            event.preventDefault();
            save.disabled = true;
            try {
                const payload = {
                    required_score: scoreSelect.value,
                    use_bayes: form.querySelector('[name="use_bayes"]')
                        .checked
                        ? "1"
                        : "0",
                    skip_rbl_checks: form.querySelector('[name="skip_rbl_checks"]').checked
                        ? "1"
                        : "0",
                    use_razor2: form.querySelector('[name="use_razor2"]')
                        .checked
                        ? "1"
                        : "0",
                    use_pyzor: form.querySelector('[name="use_pyzor"]')
                        .checked
                        ? "1"
                        : "0",
                    use_dcc: form.querySelector('[name="use_dcc"]').checked
                        ? "1"
                        : "0",
                    ok_locales: langInput.value.trim(),
                };
                await this.applySettings(await updateSettings(this.options.storage, payload), { text: "Preferences saved.", type: "success" });
                save.disabled = false;
            }
            catch (error) {
                flash(this.options.root, error instanceof Error ? error.message : "Save failed", "error");
                save.disabled = false;
            }
        });
        section.append(form);
        return section;
    }
    renderListSection(title, prefKey, action, inputType, hint, emptyMessage, maxItems) {
        const section = el("section", "card");
        section.append(el("h2", undefined, title));
        if (hint) {
            for (const paragraph of Array.isArray(hint) ? hint : [hint]) {
                section.append(el("p", "hint", paragraph));
            }
        }
        const items = sortedListValues(this.settings[prefKey] ?? "");
        if (maxItems !== undefined) {
            section.append(el("p", "hint", `${items.length} / ${maxItems} addresses`));
        }
        if (items.length === 0) {
            section.append(el("p", "muted", emptyMessage ?? `No ${title.toLowerCase()} entries.`));
        }
        else {
            const list = el("ul", "address-list");
            for (const item of items) {
                list.append(createSelectableListItem(item));
            }
            section.append(list);
            const toolbar = el("div", "list-toolbar");
            const removeSelected = el("button", "btn btn-danger btn-sm", "Remove selected");
            removeSelected.type = "button";
            removeSelected.disabled = true;
            removeSelected.addEventListener("click", () => {
                const selected = selectedCheckboxValues(list, ".list-select-checkbox");
                void this.removeListItems(action, selected, title);
            });
            wireRemoveSelectedButton(list, ".list-select-checkbox", removeSelected);
            toolbar.append(createToggleAllButton(list, ".list-select-checkbox"), removeSelected);
            section.append(toolbar);
        }
        const addRow = el("div", "inline-add mt");
        const senderEmail = this.messageContext?.senderEmail;
        const senderAlreadyListed = inputType === "email" &&
            senderEmail !== undefined &&
            senderListIncludes(items, senderEmail);
        const oppositeKey = inputType === "email" ? oppositeSenderListKey(prefKey) : null;
        const oppositeItems = oppositeKey !== null ? parseListValues(this.settings[oppositeKey] ?? "") : [];
        const senderOnOppositeList = inputType === "email" &&
            senderEmail !== undefined &&
            oppositeKey !== null &&
            senderListIncludes(oppositeItems, senderEmail);
        if (senderAlreadyListed) {
            section.append(el("p", "muted", "This sender is already on this list."));
        }
        else if (senderOnOppositeList) {
            const oppositeLabel = prefKey === "whitelist_from" ? "deny" : "allow";
            section.append(el("p", "muted", `This sender is on the ${oppositeLabel} list.`));
        }
        const atLimit = maxItems !== undefined && items.length >= maxItems;
        if (atLimit) {
            section.append(el("p", "muted", `Deny list is full (${maxItems}/${maxItems}). Remove an address to add another.`));
        }
        else {
            const input = document.createElement("input");
            input.type = inputType;
            input.placeholder = inputType === "email" ? "sender@example.com" : "Subject pattern";
            input.required = true;
            if (inputType === "email" && senderEmail && !senderAlreadyListed && !senderOnOppositeList) {
                input.value = senderEmail;
            }
            const addBtn = el("button", "btn btn-secondary btn-sm", "Add");
            addBtn.type = "button";
            addBtn.addEventListener("click", async () => {
                const value = input.value.trim();
                if (!value)
                    return;
                if (inputType === "email" && oppositeKey !== null) {
                    const opposite = parseListValues(this.settings[oppositeKey] ?? "");
                    if (senderListIncludes(opposite, value)) {
                        const oppositeLabel = prefKey === "whitelist_from" ? "deny" : "allow";
                        flash(this.options.root, `This address is on the ${oppositeLabel} list. Remove it there first.`, "error");
                        return;
                    }
                }
                if (maxItems !== undefined && items.length >= maxItems) {
                    flash(this.options.root, `Deny list is limited to ${maxItems} addresses.`, "error");
                    return;
                }
                try {
                    await this.applySettings(await updateList(this.options.storage, action, value, "add"));
                }
                catch (error) {
                    flash(this.options.root, error instanceof Error ? error.message : "Add failed", "error");
                }
            });
            addRow.append(input, addBtn);
            section.append(addRow);
        }
        return section;
    }
    renderCustomScores() {
        const section = el("section", "card");
        section.append(el("h2", undefined, "Custom Rule Scores"));
        section.append(el("p", "hint", "Per-rule score overrides stored as spam filter score rules."));
        const scores = customScores(this.settings);
        const table = el("table", "data-table");
        const thead = el("thead");
        const headRow = el("tr");
        headRow.append(el("th", "list-select-col", ""), el("th", undefined, "Rule"), el("th", undefined, "Score"), el("th", undefined, ""));
        thead.append(headRow);
        const tbody = el("tbody");
        for (const [rule, score] of scores) {
            const row = el("tr");
            const selectCell = el("td", "list-select-col");
            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.className = "list-select-checkbox";
            checkbox.value = rule;
            selectCell.append(checkbox);
            const ruleCell = el("td");
            ruleCell.append(el("code", undefined, rule));
            const scoreCell = el("td");
            const scoreInput = document.createElement("input");
            scoreInput.type = "text";
            scoreInput.value = score;
            scoreInput.className = "table-input";
            scoreCell.append(scoreInput);
            const actionCell = el("td");
            const saveBtn = el("button", "btn btn-secondary btn-sm", "Save");
            saveBtn.type = "button";
            saveBtn.addEventListener("click", async () => {
                const nextScore = scoreInput.value.trim();
                if (!nextScore)
                    return;
                try {
                    await this.applySettings(await updateCustomScore(this.options.storage, rule, nextScore, "add"), { text: `Score updated for ${rule}.`, type: "success" });
                }
                catch (error) {
                    flash(this.options.root, error instanceof Error ? error.message : "Save failed", "error");
                }
            });
            actionCell.append(saveBtn);
            row.append(selectCell, ruleCell, scoreCell, actionCell);
            tbody.append(row);
        }
        table.append(thead, tbody);
        if (scores.length > 0) {
            section.append(table);
            const toolbar = el("div", "list-toolbar");
            const removeSelected = el("button", "btn btn-danger btn-sm", "Remove selected");
            removeSelected.type = "button";
            removeSelected.disabled = true;
            removeSelected.addEventListener("click", () => {
                const selected = selectedCheckboxValues(table, ".list-select-checkbox");
                void this.removeCustomScoreRules(selected);
            });
            wireRemoveSelectedButton(table, ".list-select-checkbox", removeSelected);
            toolbar.append(createToggleAllButton(table, ".list-select-checkbox"), removeSelected);
            section.append(toolbar);
        }
        else {
            section.append(el("p", "muted", "No custom rule scores."));
        }
        const addForm = el("div", "inline-add mt");
        const ruleInput = document.createElement("input");
        ruleInput.type = "text";
        ruleInput.placeholder = "RULE_NAME";
        ruleInput.className = "table-input";
        ruleInput.required = true;
        const newScoreInput = document.createElement("input");
        newScoreInput.type = "text";
        newScoreInput.placeholder = "0.0";
        newScoreInput.className = "table-input";
        newScoreInput.required = true;
        const addBtn = el("button", "btn btn-primary btn-sm", "Add rule");
        addBtn.type = "button";
        addBtn.addEventListener("click", async () => {
            const rule = ruleInput.value.trim();
            const score = newScoreInput.value.trim();
            if (!rule || !score)
                return;
            try {
                await this.applySettings(await updateCustomScore(this.options.storage, rule, score, "add"), { text: `Added custom score for ${rule}.`, type: "success" });
            }
            catch (error) {
                flash(this.options.root, error instanceof Error ? error.message : "Add failed", "error");
            }
        });
        addForm.append(ruleInput, newScoreInput, addBtn);
        section.append(addForm);
        return section;
    }
}
export function mountEmailAppsApp(options) {
    const layout = options.layout ?? "compact";
    document.documentElement.classList.toggle("email-apps-layout-full", layout === "full");
    document.documentElement.classList.toggle("email-apps-layout-compact", layout === "compact");
    document.body?.classList.toggle("email-apps-layout-full", layout === "full");
    document.body?.classList.toggle("email-apps-layout-compact", layout === "compact");
    const app = new EmailAppsApp(options);
    void app.init();
    return app;
}
